import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, merge } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { CurrencyWalletService } from 'src/app/layouts/currency-wallet/currency-wallet.service';
import { PipesModule } from '../../pipes/pipes.module';

@UntilDestroy()
@Component({
  selector: 'cs-currency-converter',
  standalone: true,
  imports: [PipesModule],
  templateUrl: './cs-currency-converter.component.html',
  styleUrl: './cs-currency-converter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsCurrencyConverterComponent implements OnChanges, OnInit {
  constructor(
    private appService: AppService,
    private currencyWalletService: CurrencyWalletService,
    private cdr: ChangeDetectorRef, // ✨ 注入
  ) {}

  /**貨幣金額 */
  @Input({ required: true }) amount: number | null = null;

  /**貨幣類型 */
  @Input({ required: true }) currency!: string;

  /**是否將貨幣類型轉換為系統設定值 */
  @Input() isConvertToSystemSettingCurrency = false;

  /**
   * 原 CurrencyValuePipe 參數說明
   * --- 非 undefined 时候，占位符，如果是字符串，则values是 0 和 空的时候 都是显示这个字符串，如果是字符串数组，则分别对应空时候显示 和 0时候显示
   */
  @Input() placeholder?: string | string[];

  /**
   * 原 CurrencyValuePipe 參數說明
   * --- 如果是正数，前面是否添加 “+” 号
   */
  @Input() addPlus?: boolean;

  /**
   * 原 CurrencyValuePipe 參數說明
   * --- 非 undefined 时候，忽略数字小数最多8位，法币最多2位的限制，而使用此数字来限定小数位数。是数字数组的时候，则区分虚拟币[0]和法币[1]来处理
   */
  @Input() toFixed?: number | (number | undefined)[];

  /**轉換的金額 */
  protected conversionAmount: number | null = null;

  /**轉換的類型 */
  protected conversionCurrency = this.currency;

  ngOnChanges(changes: SimpleChanges): void {
    const { amount, currency } = changes;

    if (amount || currency) {
      const currentAmount = amount?.currentValue ?? this.amount;
      const currentCurrency = currency?.currentValue ?? this.currency;
      this.updateCurrencyAmount(currentAmount, currentCurrency);
    }
  }

  ngOnInit(): void {
    merge(
      this.appService.currencies$,
      this.appService.currentCurrency$,
      this.currencyWalletService.defaultToFiat$.pipe(filter(() => this.isConvertToSystemSettingCurrency)),
      this.currencyWalletService.displayInFiat$,
      this.currencyWalletService.inFiatConvertType$,
    )
      .pipe(
        untilDestroyed(this),
        debounceTime(0),
        filter(() => this.appService.currencies$.value.length > 0),
      )
      .subscribe(() => this.updateCurrencyAmount(this.amount, this.currency));
  }

  /**更新貨幣金額 */
  private updateCurrencyAmount(amount: number | null, currency: string): void {
    let targetConversionCurrency: unknown = currency;
    if (this.isConvertToSystemSettingCurrency) {
      targetConversionCurrency = this.currencyWalletService.defaultToFiat$.value.currency?.currency;
    }
    if (this.currencyWalletService.displayInFiat$.value) {
      targetConversionCurrency = this.currencyWalletService.inFiatConvertType$.value;
    }
    if (!targetConversionCurrency) {
      console.warn(`[CsCurrencyConverterComponent] Invalid currency value: ${targetConversionCurrency}.`);
      return;
    }

    const allCurrencies = this.appService.currencies$.value;
    const currencyConfig = allCurrencies?.find(x => x?.currency === targetConversionCurrency);
    if (!currencyConfig) {
      console.warn(`[CsCurrencyConverterComponent] Currency configuration not found for: ${targetConversionCurrency}.`);
      return;
    }

    this.conversionCurrency = currencyConfig.currency;
    this.conversionAmount = this.currencyWalletService.convertCurrency(amount, currency, this.conversionCurrency);
    this.cdr.markForCheck();
  }
}
